import { useTranslation } from "react-i18next"

import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import {
  DELETE_PRODUCT_POSITIONS_OVERLAY,
  EDIT_PRODUCT_POSITION_OVERLAY,
  INSTALLATION_REFERENCE_SELECTION_OVERLAY,
  NEW_PRODUCT_POSITION_OVERLAY,
  PRODUCT_CONFIGURATION_OVERLAY
} from "shared/context/overlay/types"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  ProductPositionInstallationReference,
  ProjectPermissions
} from "shared/types/__generated__/graphql"

import { useCreateProductPosition } from "entities/product-positions/api/useCreateProductPosition"
import { useUpdateProductPosition } from "entities/product-positions/api/useUpdateProductPosition"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { ProductPositionFormTabType } from "features/product-position-management/types/ProductPositionFormTabType"
import { ProductConfiguratorResult } from "widgets/product-configurator/model/useProductConfiguratorModel"

interface UseProductPositionActionHandlersArgs {
  projectID: string
  productPosition: ProductPosition
  permissions: ProjectPermissions
  onAction?: () => void
}

export type FilteredProductPositionActionHandlers = {
  onEdit: (() => void) | undefined
  onDuplicate: (() => void) | undefined
  onCopy: (() => void) | undefined
  onAssignAttachments: (() => void) | undefined
  onAddClarificationRequest: (() => void) | undefined
  onDelete: (() => void) | undefined
  onEditConfiguration: (() => void) | undefined
  onSelectInstallationReferences: (() => void) | undefined
}

export function useProductPositionActionHandlers({
  projectID,
  productPosition,
  permissions,
  onAction
}: UseProductPositionActionHandlersArgs): FilteredProductPositionActionHandlers {
  const { t } = useTranslation()
  const { activeProjectID } = useNeoletterUserProfile()
  const [createProductPosition] = useCreateProductPosition()
  const [updateProductPosition] = useUpdateProductPosition()
  const { openOverlay: deleteProductPositionsOverlay } = useOverlay(
    DELETE_PRODUCT_POSITIONS_OVERLAY
  )
  const { openOverlay: editProductPositionsOverlay } = useOverlay(
    EDIT_PRODUCT_POSITION_OVERLAY
  )
  const { openOverlay: openNewProductPositionOverlay } = useOverlay(
    NEW_PRODUCT_POSITION_OVERLAY
  )
  const { openOverlay: openInstallationReferenceSelectionOverlay } = useOverlay(
    INSTALLATION_REFERENCE_SELECTION_OVERLAY
  )
  const { openOverlay: openProductConfigurationOverlay } = useOverlay(
    PRODUCT_CONFIGURATION_OVERLAY
  )

  const { parent } = useProjectChildPosition({
    child: productPosition || null,
    projectID
  })

  const updateConfiguration = useEvent(
    (configuration: ProductConfiguratorResult) =>
      updateProductPosition({
        projectID,
        productPosition: {
          id: productPosition.id,
          variantKey: configuration.variantKey,
          configurationKey: configuration.configurationKey,
          dimensioning: configuration.externalDimensioningResult
        }
      })
  )

  const updateInstallationReferences = useEvent(
    (
      productPositionInstallationReferences:
        | ProductPositionInstallationReference[]
        | null
    ) => {
      updateProductPosition({
        projectID,
        productPosition: {
          id: productPosition.id,
          references: productPositionInstallationReferences
        }
      })
    }
  )

  const handleEditConfiguration = useEvent(() => {
    openProductConfigurationOverlay({
      configuration: {
        variantKey: productPosition.variantKey,
        configurationKey: productPosition.configurationKey,
        externalDimensioningResult: productPosition.dimensioning || null
      },
      onSubmit: updateConfiguration
    })
    onAction?.()
  })

  const handleAssignAttachments = useEvent(() => {
    editProductPositionsOverlay({
      projectID,
      productPositionID: productPosition.id,
      defaultTab: ProductPositionFormTabType.ATTACHMENTS
    })
    onAction?.()
  })

  const handleAddClarificationRequest = useEvent(() => {
    editProductPositionsOverlay({
      projectID,
      productPositionID: productPosition.id,
      defaultTab: ProductPositionFormTabType.CLARIFICATION_REQUEST
    })
    onAction?.()
  })

  const handleEdit = useEvent(() => {
    editProductPositionsOverlay({
      projectID,
      productPositionID: productPosition.id
    })
    onAction?.()
  })

  const handleDelete = useEvent(() => {
    deleteProductPositionsOverlay({
      projectID,
      productPositions: [productPosition]
    })
    onAction?.()
  })

  const handleDuplicate = useEvent(() => {
    onAction?.()
    return createProductPosition({
      productPosition: { ...productPosition, id: generateUUID() },
      displayPosition: productPosition.position + 1,
      projectID,
      parentID: parent?.id || projectID
    })
  })

  const handleCopy = useEvent(() => {
    openNewProductPositionOverlay({
      defaultValues: productPosition,
      projectID: activeProjectID || projectID,
      title: t("ProductPosition.Actions.Copy"),
      submitButtonText: t("ProductPosition.Actions.CopyAction")
    })
    onAction?.()
  })

  const handleSelectInstallationReferences = useEvent(() => {
    openInstallationReferenceSelectionOverlay({
      productPosition,
      onSubmit: updateInstallationReferences
    })
    onAction?.()
  })

  const canEdit = permissions[ProjectPermissionType.EDIT]

  return {
    onEdit: canEdit ? handleEdit : undefined,
    onDuplicate: canEdit ? handleDuplicate : undefined,
    onCopy: canEdit ? handleCopy : undefined,
    onAssignAttachments: canEdit ? handleAssignAttachments : undefined,
    onAddClarificationRequest: canEdit
      ? handleAddClarificationRequest
      : undefined,
    onDelete: canEdit ? handleDelete : undefined,
    onEditConfiguration: canEdit ? handleEditConfiguration : undefined,
    onSelectInstallationReferences: canEdit
      ? handleSelectInstallationReferences
      : undefined
  }
}
